// DispatchDeck - Time-Off Module
// Request and approval workflow for time off

import {
  db,
  functions,
  httpsCallable,
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp
} from '../firebase-config.js?v=1868';

// ============================================
// TIME-OFF STATE
// ============================================
const TimeOffState = {
  requests: [],
  people: [],
  filter: 'pending', // 'pending', 'approved', 'denied', 'all'
  selectedRequest: null,
  unsubscribers: [],
  tenantId: null,
  userRole: null,
  policy: null,          // TASK-9838 — hrTimeOffPolicy/config (null = none / not loaded)
};

// TASK-9838 — which request types a person may pick. The full list only once
// the person has a Time-Off Level that exists in the company's plan; with no
// plan or no level there is nothing paid to draw on, so it is Unpaid only
// (Chris 2026-10-13: "that should be the only thing available if there is no
// PTO"). Days On stays: it marks extra scheduled work, not an absence, and
// never draws on a plan. Bereavement is NOT offered without a plan — build it
// into the plan in the PTO plan maker if the company wants it.
const TIME_OFF_ALL_TYPES = [
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'vacation', label: 'Vacation' },
  { value: 'sick', label: 'Sick Leave' },
  { value: 'personal', label: 'Personal Day' },
  { value: 'bereavement', label: 'Bereavement' },
  { value: 'parental_leave', label: 'Parental Leave' },
  { value: 'jury_duty', label: 'Jury Duty' },
  { value: 'other', label: 'Other' },
  { value: 'days_on', label: 'Days On (Scheduled Work Days)' },
];
const TIME_OFF_NO_PLAN_TYPES = [
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'days_on', label: 'Days On (Scheduled Work Days)' },
];
const TIME_OFF_NO_PLAN_HINT = 'No time-off plan assigned — only Unpaid is available. Paid types (vacation, sick, bereavement…) come from building a plan in HR and giving this person a Time-Off Level.';
function personHasTimeOffPlan(person, policy) {
  const levelId = person && person.hrTimeOffLevel;
  if (!levelId) return false;
  const levels = policy && Array.isArray(policy.levels) ? policy.levels : [];
  return levels.some(l => l && l.id === levelId);
}
function timeOffTypeOptionsFor(person, policy) {
  return personHasTimeOffPlan(person, policy) ? TIME_OFF_ALL_TYPES : TIME_OFF_NO_PLAN_TYPES;
}

// Rebuild #request-type for the person currently picked. `keepType` (edit
// mode) is kept selectable even when it is no longer offered, labelled as
// such, so saving an edit never silently rewrites the type.
function applyRequestTypeOptions(keepType) {
  const sel = document.getElementById('request-type');
  if (!sel) return;
  const personId = document.getElementById('request-person')?.value || '';
  const person = personId ? TimeOffState.people.find(p => p.id === personId) : null;
  const options = timeOffTypeOptionsFor(person, TimeOffState.policy).slice();
  // Only an EDITED request's own type (keepType) survives when it is no longer
  // offered. A value merely left in the select from a previous pick does not —
  // switching from a planned person to one without a plan must drop Vacation
  // (COD 2026-10-14 Code Reviewer).
  const wanted = keepType || sel.value || 'unpaid';
  if (keepType && !options.some(o => o.value === keepType)) {
    const known = TIME_OFF_ALL_TYPES.find(o => o.value === keepType);
    options.push({ value: keepType, label: `${known ? known.label : keepType} (no longer offered)` });
  }
  sel.innerHTML = options.map(o => `<option value="${escapeHtml(o.value)}">${escapeHtml(o.label)}</option>`).join('');
  sel.value = options.some(o => o.value === wanted) ? wanted : 'unpaid';
  const ber = document.getElementById('timeoff-bereavement-sub-group');
  if (ber) ber.style.display = sel.value === 'bereavement' ? '' : 'none';
  const hint = document.getElementById('request-type-hint');
  if (hint) {
    const noPlan = !!person && !personHasTimeOffPlan(person, TimeOffState.policy);
    hint.textContent = noPlan ? TIME_OFF_NO_PLAN_HINT : '';
    hint.style.display = noPlan ? '' : 'none';
  }
}

// TASK-528 — Can the viewer file time off on OTHER people's behalf?
// Privileged tier mirrors the timeOff firestore.rules create rule's
// "create for anyone, any status" branch AND clock.js canRequestTimeOffForOthers:
// owner/admin/dispatcher (role) OR an active member with the `hr` module.
// Everyone else (a clock-only / timeoff-only staff member) is self-scoped.
function canRequestTimeOffForOthers() {
  const ma = window.AppState?.memberAccess;
  // No member doc → PIN/legacy/founder path. Founder + legacy single-login
  // owner keep full picker (they had it before TASK-528).
  if (!ma) return true;
  if (['revoked', 'inactive', 'pending'].includes(ma.status)) return false;
  if (ma.role === 'owner' || ma.role === 'admin' || ma.role === 'dispatcher') return true;
  // Legacy admin (admin role, no modules) already covered above.
  return Array.isArray(ma.modules) && ma.modules.includes('hr');
}

// TASK-571 — Can the viewer EDIT this submitted request?
// Editing reuses the firestore `update` rule (firestore.rules:1453 —
// dispatcher+/admin/owner OR the `hr` module — the SAME privileged tier as
// approve/deny AND the delete/Cancel rule at :1455). Only PENDING requests are
// editable: an approved/denied request is a settled decision and must NOT be
// silently changed (a re-edit would also revert status, which the audit log
// flags as `approvalReverted`).
//
// IMPORTANT: the timeOff `update` rule NOW HAS a self-edit branch (TASK-598,
// added in firestore.rules under the timeOff match) — `isOwnTimeOffRequest &&
// resource.data.status=='pending'` plus a `.diff().affectedKeys().hasOnly([...])`
// field fence, transitioning status only to pending/cancelled. That branch was
// added for the DRIVER self-service modal (dispatch.js editDriverTimeOff). This
// `timeoff.js` view does NOT yet expose self-edit — `canEditRequest` still gates
// to the privileged tier on purpose (the self-edit UI here is a flagged
// fast-follow, deliberately NOT enabled in TASK-598). Do NOT relax this gate to
// match the rule without also building the self-scoped edit UX + verifying the
// field fence holds for this surface's payload. The rule no longer blocks it; the
// product decision to surface it here is the remaining gate.
// TASK-4720 (Chris 2026-10-22) — "only the requester; office approves or
// rejects". Editing / withdrawing is now the REQUESTER's alone (the viewer's
// linked person, or the login that filed it), pending only, and the server
// (saveTimeOffRequest) enforces the same thing. Office users Review instead.
function isOwnRequest(request) {
  if (!request) return false;
  if ((window.AppState?.tenant || 'demo') === 'demo') return true; // demo is open (server + rules)
  const self = window.AppState?.memberAccess?.personId || null;
  const uid = window.AppState?.user?.uid || null;
  return (!!self && request.personId === self) || (!!uid && request.submittedByUid === uid);
}
function canEditRequest(request) {
  if (!request || request.status !== 'pending') return false;
  return isOwnRequest(request);
}

// ============================================
// INITIALIZE TIME-OFF VIEW
// ============================================
export function initTimeOff(container, tenantId) {
  console.log('[TimeOff] Initializing for tenant:', tenantId);

  // Guard against null tenant
  if (!tenantId) {
    console.error('[TimeOff] No tenant ID provided');
    container.innerHTML = `
      <div class="empty-state">
        <div class="empty-state-icon">⚠️</div>
        <div class="empty-state-title">Account Setup Issue</div>
        <div class="empty-state-text">
          Unable to load time-off. Please try logging out and back in.
        </div>
        <button class="btn btn-primary" onclick="window.handleLogout()">
          <span class="material-icons">logout</span> Log Out
        </button>
      </div>
    `;
    return () => {};
  }

  TimeOffState.tenantId = tenantId;
  TimeOffState.userRole = window.AppState?.userRole || 'driver';

  // Render the time-off UI
  container.innerHTML = getTimeOffHTML();

  // Set up filter switching
  setupFilters();

  // Set up modal handlers
  setupModals();

  // Subscribe to real-time data
  subscribeToData(tenantId);

  // Return cleanup function
  return () => {
    TimeOffState.unsubscribers.forEach(unsub => unsub());
    TimeOffState.unsubscribers = [];
    TimeOffState.selectedRequest = null;
  };
}

// ============================================
// HTML TEMPLATE
// ============================================
function getTimeOffHTML() {
  return `
    <div class="timeoff-view">
      <!-- Header -->
      <div style="text-align:center;margin-bottom:var(--spacing-sm);">
        <h1 style="margin:0;font-size:1.5rem;font-weight:700;">Time Off</h1>
        <span class="request-count" id="requests-count">0 requests</span>
      </div>
      <div class="view-header" style="justify-content:center;">
        <div class="view-actions">
          <button class="btn btn-primary" onclick="window.TimeOffModule.showNewRequest()">
            <span class="material-icons">add</span>
            Request Time Off
          </button>
        </div>
      </div>

      <!-- Filters -->
      <div class="timeoff-filters">
        <button class="filter-btn active" data-filter="pending">
          <span class="material-icons">pending</span>
          Pending
        </button>
        <button class="filter-btn" data-filter="approved">
          <span class="material-icons">check_circle</span>
          Approved
        </button>
        <button class="filter-btn" data-filter="denied">
          <span class="material-icons">cancel</span>
          Denied
        </button>
        <button class="filter-btn" data-filter="all">
          <span class="material-icons">list</span>
          All
        </button>
      </div>

      <!-- Requests List -->
      <div class="timeoff-content">
        <div class="timeoff-list" id="timeoff-list">
          <div class="loading-state">
            <div class="loading-spinner"></div>
            <p>Loading requests...</p>
          </div>
        </div>
      </div>
    </div>

    <!-- New Request Modal -->
    <div class="modal hidden" id="modal-request">
      <div class="modal-content">
        <div class="modal-header">
          <h3 id="modal-request-title">Request Time Off</h3>
          <button class="btn-icon" onclick="window.TimeOffModule.closeModal('request')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <form id="form-request" novalidate onsubmit="window.TimeOffModule.saveRequest(event)">
          <input type="hidden" id="request-id">

          <div class="form-group">
            <label for="request-person">Employee *</label>
            <select id="request-person" required>
              <option value="">Select employee...</option>
            </select>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="request-start">Start Date *</label>
              <input type="date" id="request-start" required onchange="var _e=document.getElementById('request-end');if(_e&&this.value&&_e.value<this.value)_e.value=this.value;">
            </div>
            <div class="form-group">
              <label for="request-end">End Date *</label>
              <input type="date" id="request-end" required>
            </div>
          </div>

          <div class="form-group">
            <label for="request-type">Type *</label>
            <select id="request-type" required onchange="document.getElementById('timeoff-bereavement-sub-group').style.display = this.value === 'bereavement' ? '' : 'none';">
              <!-- TASK-9838: options are built per person by applyRequestTypeOptions — Unpaid only without a plan/level -->
              <option value="unpaid">Unpaid</option>
            </select>
            <div id="request-type-hint" style="display:none;margin-top:6px;font-size:0.82rem;color:var(--text-secondary);"></div>
          </div>
          <div id="timeoff-bereavement-sub-group" style="display:none;">
            <div class="form-group">
              <label for="timeoff-bereavement-level">Relationship Level *</label>
              <select id="timeoff-bereavement-level" class="form-control" onchange="window.TimeOffModule.autofillBereavementHours()">
                <option value="">Select relationship...</option>
                <option value="level1">Level 1 — Spouse, child, parent, grandchild</option>
                <option value="level2">Level 2 — Sibling, in-laws</option>
                <option value="level3">Level 3 — Household member</option>
                <option value="level4">Level 4 — Grandparent, grandparent-in-law</option>
                <option value="level5">Level 5 — Loss of pregnancy</option>
                <option value="level6">Level 6 — Aunt, uncle, cousin, niece, nephew</option>
                <option value="level7">Level 7 — Distant relative, close friend</option>
              </select>
            </div>
            <div class="form-group">
              <label style="display:flex;align-items:center;gap:var(--spacing-sm);cursor:pointer;">
                <input type="checkbox" id="timeoff-bereavement-travel" onchange="window.TimeOffModule.autofillBereavementHours()">
                <span style="font-size:0.85rem;">Travel days (funeral 250+ miles away)</span>
              </label>
            </div>
          </div>

          <div class="form-group">
            <label for="request-hours">Hours *</label>
            <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
              <input type="number" id="request-hours" min="1" max="2000" value="8" required
                style="max-width:100px;" oninput="window.TimeOffModule.updateHoursHint()">
              <span id="request-hours-hint" style="font-size:0.8rem;color:var(--text-secondary);">1 day (8 hrs)</span>
            </div>
          </div>

          <div class="form-group">
            <label for="request-reason">Reason/Notes</label>
            <textarea id="request-reason" rows="3" placeholder="Optional notes..."></textarea>
          </div>

          <div class="form-actions">
            <button type="button" class="btn btn-secondary" onclick="window.TimeOffModule.closeModal('request')">Cancel</button>
            <button type="submit" class="btn btn-primary">Submit Request</button>
          </div>
        </form>

        <!-- My Time Off — upcoming + past history for the selected person -->
        <div class="collapsible-section timeoff-history-section" id="timeoff-history-section" style="margin-top:var(--spacing-md);">
          <div class="collapsible-header" onclick="window.TimeOffModule.toggleHistory()">
            <span class="material-icons collapsible-arrow">expand_more</span>
            <span class="collapsible-title">My Time Off</span>
          </div>
          <div class="collapsible-body" id="timeoff-history-body" style="max-height:none;">
            <div class="timeoff-history-loading" style="text-align:center;padding:var(--spacing-sm);color:var(--text-secondary);font-size:0.85rem;">
              Loading...
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Review Request Modal (for managers) -->
    <div class="modal hidden" id="modal-review">
      <div class="modal-content">
        <div class="modal-header">
          <h3>Review Request</h3>
          <button class="btn-icon" onclick="window.TimeOffModule.closeModal('review')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div id="review-content" class="review-detail">
          <!-- Populated dynamically -->
        </div>
        <div class="review-actions" id="review-actions">
          <!-- Action buttons for pending requests -->
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div class="modal hidden" id="modal-delete-request">
      <div class="modal-content modal-small">
        <div class="modal-header">
          <h3>Withdraw Request</h3>
        </div>
        <div class="modal-body">
          <p>Withdraw this time-off request? It is deleted, and you can send a new one.</p>
        </div>
        <div class="form-actions">
          <button class="btn btn-secondary" onclick="window.TimeOffModule.closeModal('delete-request')">Keep</button>
          <button class="btn btn-danger" id="btn-confirm-delete-request">Withdraw</button>
        </div>
      </div>
    </div>
  `;
}

// ============================================
// FILTER SWITCHING
// ============================================
function setupFilters() {
  document.querySelectorAll('.timeoff-filters .filter-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const filter = btn.dataset.filter;
      switchFilter(filter);
    });
  });
}

function switchFilter(filter) {
  TimeOffState.filter = filter;

  document.querySelectorAll('.timeoff-filters .filter-btn').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.filter === filter);
  });

  renderRequestsList();
}

// ============================================
// MODAL HANDLERS
// ============================================
function setupModals() {
  // Close modal on backdrop click (but not for form modals to prevent data loss)
  document.querySelectorAll('.modal').forEach(modal => {
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        // Don't close modals with form inputs on backdrop click (prevents data loss)
        const hasFormInputs = modal.querySelector('input, select, textarea');
        if (hasFormInputs) return;

        modal.classList.add('hidden');
      }
    });
  });
}

// ============================================
// DATA SUBSCRIPTIONS
// ============================================
function subscribeToData(tenantId) {
  const tenant = tenantId === 'demo-tenant' ? 'demo' : tenantId;

  // TASK-9838 — the company plan decides which request types a person may
  // pick (see timeOffTypeOptionsFor). Live so a plan built in HR applies
  // without a reload; a read failure just leaves the Unpaid-only list.
  try {
    const unsubPolicy = onSnapshot(doc(db, 'tenants', tenant, 'hrTimeOffPolicy', 'config'), (snap) => {
      TimeOffState.policy = snap.exists() ? (snap.data() || null) : null;
      const modal = document.getElementById('modal-request');
      if (modal && !modal.classList.contains('hidden')) applyRequestTypeOptions();
    }, (err) => {
      console.warn('[TimeOff] policy read failed:', err?.message);
      TimeOffState.policy = null;
    });
    TimeOffState.unsubscribers.push(unsubPolicy);
  } catch (err) {
    console.warn('[TimeOff] policy subscribe error:', err?.message);
  }

  // Subscribe to time-off requests
  const requestsQuery = query(
    collection(db, 'tenants', tenant, 'timeOff'),
    orderBy('startDate', 'desc')
  );
  const unsubRequests = onSnapshot(requestsQuery, (snapshot) => {
    TimeOffState.requests = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      startDate: doc.data().startDate?.toDate ? doc.data().startDate.toDate() : new Date(doc.data().startDate),
      endDate: doc.data().endDate?.toDate ? doc.data().endDate.toDate() : new Date(doc.data().endDate)
    }));
    renderRequestsList();
    // If the request modal is open, refresh the My Time Off history so a newly
    // submitted request shows up immediately without re-opening the modal.
    if (!document.getElementById('modal-request')?.classList.contains('hidden')) {
      renderHistory();
    }
  }, (error) => {
    console.error('[TimeOff] Error loading requests:', error);
  });
  TimeOffState.unsubscribers.push(unsubRequests);

  // Subscribe to people for dropdown
  const peopleQuery = query(
    collection(db, 'tenants', tenant, 'people'),
    orderBy('name')
  );
  const unsubPeople = onSnapshot(peopleQuery, (snapshot) => {
    TimeOffState.people = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    updatePersonDropdown();
  }, (error) => {
    console.error('[TimeOff] Error loading people:', error);
  });
  TimeOffState.unsubscribers.push(unsubPeople);
}

function updatePersonDropdown() {
  const select = document.getElementById('request-person');
  if (!select) return;

  const currentValue = select.value;
  select.innerHTML = '<option value="">Select employee...</option>' +
    TimeOffState.people.map(p =>
      `<option value="${p.id}">${escapeHtml(p.name)}</option>`
    ).join('');
  if (currentValue) select.value = currentValue;
}

// ============================================
// RENDER REQUESTS LIST
// ============================================
function renderRequestsList() {
  const container = document.getElementById('timeoff-list');
  if (!container) return;

  // Filter requests
  let filteredRequests = TimeOffState.requests;
  if (TimeOffState.filter !== 'all') {
    filteredRequests = filteredRequests.filter(r => r.status === TimeOffState.filter);
  }

  // Update count
  const countEl = document.getElementById('requests-count');
  if (countEl) {
    countEl.textContent = `${filteredRequests.length} request${filteredRequests.length !== 1 ? 's' : ''}`;
  }

  if (filteredRequests.length === 0) {
    container.innerHTML = `
      <div class="empty-state">
        <div class="empty-state-icon">📅</div>
        <div class="empty-state-title">No ${TimeOffState.filter} requests</div>
        <div class="empty-state-text">
          ${TimeOffState.filter === 'pending'
            ? 'No time-off requests are waiting for approval.'
            : 'No requests found with this status.'}
        </div>
      </div>
    `;
    return;
  }

  container.innerHTML = filteredRequests.map(request => {
    const person = TimeOffState.people.find(p => p.id === request.personId);
    const days = calculateDays(request.startDate, request.endDate);

    return `
      <div class="timeoff-card status-${request.status}" data-id="${request.id}">
        <div class="timeoff-card-header">
          <div class="timeoff-card-person">
            <div class="person-avatar" style="background: ${person?.color || '#3B82F6'}">
              ${getInitials(person?.name || request.personName)}
            </div>
            <div class="person-info">
              <div class="person-name">${escapeHtml(person?.name || request.personName || 'Unknown')}</div>
              <div class="person-role">${escapeHtml(person?.role || '')}</div>
            </div>
          </div>
          <span class="badge badge-${request.status}">${capitalizeFirst(request.status)}</span>
          ${request.changeRequestFor ? `<span class="badge badge-change-request badge-sm" style="margin-left:4px;"><span class="material-icons">edit_note</span>${request.changeType === 'cancel' ? 'Cancellation' : 'Change'} Request</span>` : ''}
        </div>
        <div class="timeoff-card-body">
          <div class="timeoff-dates">
            <span class="material-icons">date_range</span>
            <span>${formatDate(request.startDate)} - ${formatDate(request.endDate)}</span>
            <span class="timeoff-days">(${days} day${days !== 1 ? 's' : ''})</span>
          </div>
          <div class="timeoff-type">
            <span class="material-icons">${getTypeIcon(request.type)}</span>
            <span>${formatType(request.type)}</span>
          </div>
          ${request.reason ? `
            <div class="timeoff-reason">
              ${escapeHtml(truncate(request.reason, 100))}
            </div>
          ` : ''}
          ${request.status === 'denied' && request.reviewNote ? `
            <div class="timeoff-reason" style="color:#dc2626;">
              Denied: ${escapeHtml(truncate(request.reviewNote, 100))}
            </div>
          ` : ''}
        </div>
        <div class="timeoff-card-actions">
          ${request.status === 'pending' ? `
            <button class="btn btn-sm btn-primary" onclick="window.TimeOffModule.reviewRequest('${request.id}')">
              <span class="material-icons">visibility</span> Review
            </button>
            ${canEditRequest(request) ? `
              <button class="btn btn-sm btn-secondary" onclick="window.TimeOffModule.editRequest('${request.id}')" title="Edit">
                <span class="material-icons">edit</span> Edit
              </button>
            ` : ''}
            ${canEditRequest(request) ? `<button class="btn-icon" onclick="window.TimeOffModule.deleteRequest('${request.id}')" title="Withdraw" aria-label="Withdraw request">
              <span class="material-icons">close</span>
            </button>` : ''}
          ` : `
            <button class="btn btn-sm btn-secondary" onclick="window.TimeOffModule.viewRequest('${request.id}')">
              <span class="material-icons">visibility</span> View
            </button>
          `}
        </div>
      </div>
    `;
  }).join('');
}

// ============================================
// CRUD OPERATIONS
// ============================================
function showNewRequest() {
  document.getElementById('modal-request-title').textContent = 'Request Time Off';
  document.getElementById('form-request').reset();
  document.getElementById('request-id').value = '';

  // Set dates to tomorrow and day after
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  document.getElementById('request-start').value = formatDateInput(tomorrow);
  document.getElementById('request-end').value = formatDateInput(tomorrow);

  updatePersonDropdown();

  // Default the person dropdown to the signed-in member's personId, if any.
  // For SPA members, this also pins the My Time Off history to their own requests.
  // Admins/owners can pick a different person and history will re-render.
  const memberPid = window.AppState?.memberAccess?.personId || null;
  if (memberPid) {
    const select = document.getElementById('request-person');
    if (select && Array.from(select.options).some(o => o.value === memberPid)) {
      select.value = memberPid;
    }
  }

  // TASK-528 self-scope: a non-privileged member may only request their OWN
  // time off. Lock the person picker to their linked personId. Privileged
  // viewers (dispatcher+/admin/owner OR HR-module clerk filing on a driver's
  // behalf) keep the full picker. Mirrors the tightened timeOff create rule
  // and the Clock-hub showNewTimeOffRequest lock.
  const personSelect = document.getElementById('request-person');
  if (personSelect) {
    if (!canRequestTimeOffForOthers()) {
      if (memberPid && Array.from(personSelect.options).some(o => o.value === memberPid)) {
        personSelect.value = memberPid;
      }
      personSelect.dataset.lockedSelf = memberPid || '';
      personSelect.setAttribute('aria-readonly', 'true');
      personSelect.style.pointerEvents = 'none';
      personSelect.style.background = 'var(--bg-subtle, #F8FAFC)';
      personSelect.onchange = () => {
        const lock = personSelect.dataset.lockedSelf || '';
        if (lock) personSelect.value = lock;
        applyRequestTypeOptions(); // TASK-9838
        renderHistory();
      };
    } else {
      delete personSelect.dataset.lockedSelf;
      personSelect.removeAttribute('aria-readonly');
      personSelect.style.pointerEvents = '';
      personSelect.style.background = '';
      // Wire person change to re-render history (idempotent — replaces handler)
      personSelect.onchange = () => { applyRequestTypeOptions(); renderHistory(); }; // TASK-9838
    }
  }
  applyRequestTypeOptions(); // TASK-9838 — type list follows the picked person

  // Default the section to open on desktop, collapsed on mobile
  const section = document.getElementById('timeoff-history-section');
  if (section) {
    const isMobile = window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
    section.classList.toggle('open', !isMobile);
  }

  renderHistory();
  document.getElementById('modal-request').classList.remove('hidden');
}

// TASK-571 — Open the request form PRE-FILLED to EDIT a pending request.
// Reuses the existing add-request form + the saveRequest update branch (a
// populated #request-id hidden field routes saveRequest to updateDoc). No new
// save path. Only the requester's own pending request, or a privileged
// reviewer's, is editable (canEditRequest); an approved/denied request is not.
function editRequest(id) {
  const request = TimeOffState.requests.find(r => r.id === id);
  if (!request) return;
  if (!canEditRequest(request)) {
    window.showToast?.('This request can no longer be edited', 'error');
    return;
  }

  // Start from the same clean form state showNewRequest builds, then override.
  showNewRequest();

  document.getElementById('modal-request-title').textContent = 'Edit Request';
  document.querySelector('#form-request button[type="submit"]').textContent = 'Save Changes';

  // The hidden id is what makes saveRequest take the update branch (:818).
  document.getElementById('request-id').value = id;

  // Person — set then respect the same self-lock showNewRequest applied.
  const personSelect = document.getElementById('request-person');
  if (personSelect && request.personId &&
      Array.from(personSelect.options).some(o => o.value === request.personId)) {
    personSelect.value = request.personId;
  }

  // Dates
  document.getElementById('request-start').value = formatDateInput(request.startDate);
  document.getElementById('request-end').value = formatDateInput(request.endDate);

  // Type (+ reveal bereavement sub-group if needed). TASK-9838: the list is
  // per person; the request's own type stays selectable even if no longer
  // offered, so an edit never silently rewrites it.
  applyRequestTypeOptions(request.type || 'unpaid');
  const typeSelect = document.getElementById('request-type');
  if (typeSelect) {
    typeSelect.value = request.type || 'unpaid';
    const berGroup = document.getElementById('timeoff-bereavement-sub-group');
    if (berGroup) berGroup.style.display = request.type === 'bereavement' ? '' : 'none';
  }
  if (request.type === 'bereavement') {
    const lvl = document.getElementById('timeoff-bereavement-level');
    if (lvl) lvl.value = request.bereavementLevel || '';
    const trav = document.getElementById('timeoff-bereavement-travel');
    if (trav) trav.checked = !!request.bereavementTravel;
  }

  // Hours + reason
  const days = calculateDays(request.startDate, request.endDate);
  document.getElementById('request-hours').value = request.hours || days * 8;
  document.getElementById('request-reason').value = request.reason || '';
  updateHoursHint();

  // Refresh the My Time Off history for the resolved person.
  renderHistory();
}

// ============================================
// MY TIME OFF — upcoming + past history under the request form
// ============================================
function toggleHistory() {
  const section = document.getElementById('timeoff-history-section');
  if (!section) return;
  section.classList.toggle('open');
}

function renderHistory() {
  const body = document.getElementById('timeoff-history-body');
  if (!body) return;

  // Resolve "current person" — explicit dropdown selection wins so admins
  // opening on behalf of someone get the right history; falls back to the
  // signed-in member's personId for staff opening their own form.
  const dropdownPid = document.getElementById('request-person')?.value || '';
  const memberPid = window.AppState?.memberAccess?.personId || '';
  const personId = dropdownPid || memberPid;

  if (!personId) {
    body.innerHTML = `
      <div style="text-align:center;padding:var(--spacing-sm);color:var(--text-secondary);font-size:0.85rem;">
        Select an employee to see their time off.
      </div>
    `;
    return;
  }

  // Use the live snapshot from TimeOffState.requests — it's already subscribed
  // to all tenant requests, sorted by startDate desc. Filter client-side to
  // avoid a second Firestore listener. Spec asks for limit 15 over the
  // ordered set; we partition into upcoming (startDate >= today) and past
  // (startDate < today, capped at 10).
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const personRequests = TimeOffState.requests
    .filter(r => r.personId === personId)
    .slice(0, 15);

  const upcoming = personRequests
    .filter(r => r.status === 'approved' && r.startDate >= today)
    // Order ascending for upcoming (next first), since TimeOffState is desc.
    .slice()
    .sort((a, b) => a.startDate - b.startDate);

  const past = personRequests
    .filter(r => (r.status === 'approved' || r.status === 'denied') && r.startDate < today)
    .slice(0, 10);

  body.innerHTML = `
    <div class="timeoff-history-group">
      <div class="timeoff-history-heading">Upcoming</div>
      ${upcoming.length === 0
        ? `<div class="timeoff-history-empty">No upcoming time off yet.</div>`
        : upcoming.map(renderHistoryRow).join('')}
    </div>
    <div class="timeoff-history-group" style="margin-top:var(--spacing-sm);">
      <div class="timeoff-history-heading">Past</div>
      ${past.length === 0
        ? `<div class="timeoff-history-empty">No past requests.</div>`
        : past.map(renderHistoryRow).join('')}
    </div>
  `;
}

function renderHistoryRow(req) {
  const days = calculateDays(req.startDate, req.endDate);
  const dateLabel = sameDay(req.startDate, req.endDate)
    ? formatDate(req.startDate)
    : `${formatDate(req.startDate)} – ${formatDate(req.endDate)}`;
  return `
    <div class="timeoff-history-row">
      <div class="timeoff-history-main">
        <div class="timeoff-history-type">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;">${getTypeIcon(req.type)}</span>
          ${escapeHtml(formatType(req.type))}
        </div>
        <div class="timeoff-history-dates">${escapeHtml(dateLabel)} · ${req.hours || days * 8} hr${(req.hours || days * 8) !== 1 ? 's' : ''}</div>
      </div>
      <span class="badge badge-${req.status}">${capitalizeFirst(req.status)}</span>
    </div>
  `;
}

// TASK-571 — Do two inclusive date ranges overlap? Compared at day granularity
// (normalize to local midnight) so a same-day request counts as an overlap.
function rangesOverlap(aStart, aEnd, bStart, bEnd) {
  const norm = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x.getTime(); };
  const as = norm(aStart), ae = norm(aEnd), bs = norm(bStart), be = norm(bEnd);
  return as <= be && bs <= ae;
}

function sameDay(a, b) {
  if (!a || !b) return false;
  const da = a instanceof Date ? a : new Date(a);
  const db = b instanceof Date ? b : new Date(b);
  return da.getFullYear() === db.getFullYear()
    && da.getMonth() === db.getMonth()
    && da.getDate() === db.getDate();
}

function viewRequest(id) {
  const request = TimeOffState.requests.find(r => r.id === id);
  if (!request) return;

  const person = TimeOffState.people.find(p => p.id === request.personId);
  const days = calculateDays(request.startDate, request.endDate);

  const content = document.getElementById('review-content');
  const bereavementInfo = request.type === 'bereavement' && request.bereavementLevel
    ? `<div class="detail-row">
        <span class="detail-label">Relationship</span>
        <span class="detail-value">${formatBereavementLevel(request.bereavementLevel)}${request.bereavementTravel ? ' + Travel' : ''}</span>
      </div>` : '';

  content.innerHTML = `
    <div class="review-header">
      <div class="person-avatar large" style="background: ${person?.color || '#3B82F6'}">
        ${getInitials(person?.name || request.personName)}
      </div>
      <div class="review-person-info">
        <div class="review-person-name">${escapeHtml(person?.name || request.personName)}</div>
        <div class="review-person-role">${escapeHtml(person?.role || '')}</div>
      </div>
    </div>
    <div class="review-details">
      <div class="detail-row">
        <span class="detail-label">Status</span>
        <span class="badge badge-${request.status}">${capitalizeFirst(request.status)}</span>
      </div>
      <div class="detail-row">
        <span class="detail-label">Type</span>
        <span class="detail-value">${formatType(request.type)}</span>
      </div>
      ${bereavementInfo}
      <div class="detail-row">
        <span class="detail-label">Dates</span>
        <span class="detail-value">${formatDate(request.startDate)} - ${formatDate(request.endDate)}</span>
      </div>
      <div class="detail-row">
        <span class="detail-label">Duration</span>
        <span class="detail-value">${days} day${days !== 1 ? 's' : ''}</span>
      </div>
      ${request.reason ? `
        <div class="detail-row full">
          <span class="detail-label">Reason</span>
          <p class="detail-value">${escapeHtml(request.reason)}</p>
        </div>
      ` : ''}
      ${request.reviewedBy ? `
        <div class="detail-row">
          <span class="detail-label">Reviewed By</span>
          <span class="detail-value">${escapeHtml(request.reviewedByName || request.reviewedBy)}</span>
        </div>
      ` : ''}
      ${request.reviewNote ? `
        <div class="detail-row full">
          <span class="detail-label">${request.status === 'denied' ? 'Denial Reason' : 'Manager Note'}</span>
          <p class="detail-value">${escapeHtml(request.reviewNote)}</p>
        </div>
      ` : ''}
      ${request.reviewedAt ? `
        <div class="detail-row">
          <span class="detail-label">Reviewed At</span>
          <span class="detail-value">${formatDateTime(request.reviewedAt)}</span>
        </div>
      ` : ''}
    </div>
  `;

  const actions = document.getElementById('review-actions');
  actions.innerHTML = `
    <button class="btn btn-secondary" onclick="window.TimeOffModule.closeModal('review')">Close</button>
  `;

  document.getElementById('modal-review').classList.remove('hidden');
}

function reviewRequest(id) {
  const request = TimeOffState.requests.find(r => r.id === id);
  if (!request) return;

  TimeOffState.selectedRequest = request;

  const person = TimeOffState.people.find(p => p.id === request.personId);
  const days = calculateDays(request.startDate, request.endDate);

  const reviewBereavementInfo = request.type === 'bereavement' && request.bereavementLevel
    ? `<div class="detail-row">
        <span class="detail-label">Relationship</span>
        <span class="detail-value">${formatBereavementLevel(request.bereavementLevel)}${request.bereavementTravel ? ' + Travel' : ''}</span>
      </div>` : '';

  const content = document.getElementById('review-content');
  content.innerHTML = `
    <div class="review-header">
      <div class="person-avatar large" style="background: ${person?.color || '#3B82F6'}">
        ${getInitials(person?.name || request.personName)}
      </div>
      <div class="review-person-info">
        <div class="review-person-name">${escapeHtml(person?.name || request.personName)}</div>
        <div class="review-person-role">${escapeHtml(person?.role || '')}</div>
      </div>
    </div>
    <div class="review-details">
      <div class="detail-row">
        <span class="detail-label">Type</span>
        <span class="detail-value">${formatType(request.type)}</span>
      </div>
      ${reviewBereavementInfo}
      <div class="detail-row">
        <span class="detail-label">Dates</span>
        <span class="detail-value">${formatDate(request.startDate)} - ${formatDate(request.endDate)}</span>
      </div>
      <div class="detail-row">
        <span class="detail-label">Duration</span>
        <span class="detail-value">${days} day${days !== 1 ? 's' : ''}</span>
      </div>
      ${request.reason ? `
        <div class="detail-row full">
          <span class="detail-label">Reason</span>
          <p class="detail-value">${escapeHtml(request.reason)}</p>
        </div>
      ` : ''}
    </div>
    <div class="review-notes">
      <label for="review-note">Manager Notes (optional)</label>
      <textarea id="review-note" rows="2" maxlength="300" placeholder="Optional — texted to the employee with the decision. Keep it schedule-related (no medical detail)."></textarea>
    </div>
  `;

  const actions = document.getElementById('review-actions');
  actions.innerHTML = `
    <button class="btn btn-secondary" onclick="window.TimeOffModule.closeModal('review')">Cancel</button>
    <button class="btn btn-danger" onclick="window.TimeOffModule.denyRequest('${id}')">
      <span class="material-icons">close</span> Deny
    </button>
    <button class="btn btn-primary" onclick="window.TimeOffModule.approveRequest('${id}')">
      <span class="material-icons">check</span> Approve
    </button>
  `;

  document.getElementById('modal-review').classList.remove('hidden');
}

async function saveRequest(e) {
  e.preventDefault();
  if (!window.validateRequiredFields(e.target)) return;
  // End-before-start guard (TITS 2026-09-23). firestore.rules refuses it too;
  // this is the plain message before the write.
  {
    const _s = document.getElementById('request-start')?.value;
    const _en = document.getElementById('request-end')?.value;
    if (_s && _en && _en < _s) { window.showToast?.('End date cannot be before start date', 'error'); return; }
  }

  const id = document.getElementById('request-id').value;
  let personId = document.getElementById('request-person').value;

  // TASK-528 self-scope enforcement (defense in depth — the UI lock in
  // showNewRequest is bypassable). A non-privileged member may ONLY request
  // their OWN time off. The firestore rule is the real backstop (self-create
  // requires personId == own linked person + status:'pending').
  if (!canRequestTimeOffForOthers()) {
    const selfId = window.AppState?.memberAccess?.personId || null;
    if (!selfId) {
      window.showToast?.('Your login isn\'t linked to a roster person yet — ask an admin.', 'error');
      return;
    }
    if (personId && personId !== selfId) {
      window.showToast?.('You can only request your own time off', 'error');
      return;
    }
    personId = selfId;
  }

  const person = TimeOffState.people.find(p => p.id === personId);

  // TASK-571 — Duplicate-date guard. Before creating a NEW request (and on
  // edit, excluding the request being edited), check for an existing pending
  // or approved request by the SAME person whose date range OVERLAPS the new
  // dates. Denied requests don't block (they're settled negatives). If an
  // overlap is found, offer to edit the existing one instead of piling up a
  // 3rd dupe (Lacey's report). The check only NARROWS new dupes; it never
  // blocks a legit edit of an existing request (that request is excluded by id).
  const newStart = new Date(document.getElementById('request-start').value);
  const newEnd = new Date(document.getElementById('request-end').value);
  if (newStart && newEnd && !isNaN(newStart) && !isNaN(newEnd)) {
    const overlap = TimeOffState.requests.find(r =>
      r.id !== id &&
      r.personId === personId &&
      (r.status === 'pending' || r.status === 'approved') &&
      r.startDate instanceof Date && r.endDate instanceof Date &&
      rangesOverlap(newStart, newEnd, r.startDate, r.endDate)
    );
    if (overlap) {
      const sameDates = sameDay(overlap.startDate, newStart) && sameDay(overlap.endDate, newEnd);
      const label = sameDates ? 'that date' : 'overlapping dates';
      const proceedToEdit = await window.showConfirm?.(
        'Existing request found',
        `There's already a ${overlap.status} request for ${person?.name || 'this employee'} on ${label} ` +
        `(${formatDate(overlap.startDate)} - ${formatDate(overlap.endDate)}). ` +
        `Edit the existing one instead of creating another?`,
        { confirmText: 'Edit existing', cancelText: 'Go back' }
      );
      // Either choice stops the duplicate create. "Edit existing" opens that
      // request for editing; "Go back" just returns to the form unchanged.
      if (proceedToEdit && canEditRequest(overlap)) {
        editRequest(overlap.id);
      } else if (proceedToEdit) {
        // Overlapping request exists but viewer can't edit it (approved, or
        // not theirs) — keep them from stacking a dupe (TASK-4720 wording).
        window.showToast?.(`Time off is already requested for ${formatDate(overlap.startDate)} - ${formatDate(overlap.endDate)}. Withdraw that request first.`, 'error');
      }
      return;
    }
  }

  // TASK-4720 — saveTimeOffRequest is the only write path: it refuses an
  // overlap server-side (the check above is the friendly early warning, the
  // server is the guarantee) and lets only the requester edit their own.
  const type = document.getElementById('request-type').value;
  const payload = {
    tenantId: window.AppState?.tenant || 'demo',
    personId,
    startDate: document.getElementById('request-start').value,
    endDate: document.getElementById('request-end').value,
    type,
    hours: parseInt(document.getElementById('request-hours')?.value) || 8,
    reason: document.getElementById('request-reason').value.trim(),
    source: 'timeoff',
  };

  if (type === 'bereavement') {
    const bereavementLevel = document.getElementById('timeoff-bereavement-level')?.value || '';
    const bereavementTravel = document.getElementById('timeoff-bereavement-travel')?.checked || false;
    if (bereavementLevel) payload.bereavementLevel = bereavementLevel;
    if (bereavementTravel) payload.bereavementTravel = true;
  }

  try {
    const fn = httpsCallable(functions, 'saveTimeOffRequest');
    if (id) {
      await fn({ ...payload, action: 'update', requestId: id });
      window.showToast?.('Request updated', 'success');
    } else {
      await fn({ ...payload, action: 'create' });
      window.showToast?.('Request submitted', 'success');
    }

    closeModal('request');
  } catch (error) {
    console.error('[TimeOff] Error saving request:', error);
    window.showToast?.(error?.message || 'Error submitting request', 'error');
  }
}

// TASK-1136 — approve/deny now flow through the adminManageTimeOff callable
// (role-gated + self-approval-blocked + server-side audit; the decision SMS —
// including the deny reason — rides the onTimeOffUpdate trigger). Raw
// updateDoc paths and the client logAudit duplicates are DELETED; the server
// _writeAudit row is authoritative. The review modal's "Manager Notes" input
// is the unified `reviewNote` field — on deny it doubles as the reason texted
// to the requester. DEMO PARITY: anon demo users have no users/{uid} doc so
// the callable can't resolve their tenant; rules leave demo open — keep the
// pre-1136 raw write for demo ONLY.
async function _decideRequestViaCallable(id, action, note) {
  const tenant = window.AppState?.tenant || 'demo';
  if (tenant === 'demo') {
    await updateDoc(doc(db, 'tenants', tenant, 'timeOff', id), {
      status: action === 'approve' ? 'approved' : 'denied',
      reviewedAt: Timestamp.now(),
      reviewedBy: window.AppState?.user?.email || 'Manager',
      // TASK-1129 — demo parity for the attribution stamp (server stamps it
      // for real tenants via adminManageTimeOff).
      reviewedByName: (window.AppState?.memberAccess?.displayName || window.AppState?.user?.displayName
        || (window.AppState?.user?.email || '').split('@')[0] || null),
      reviewNote: note || '',
      updatedAt: Timestamp.now()
    });
    return;
  }
  const fn = httpsCallable(functions, 'adminManageTimeOff');
  await fn({ requestId: id, action, reason: note || '' });
}

async function approveRequest(id) {
  const note = (document.getElementById('review-note')?.value || '').trim().slice(0, 300);

  try {
    await _decideRequestViaCallable(id, 'approve', note);
    window.showToast?.('Request approved', 'success');
    closeModal('review');
  } catch (error) {
    console.error('[TimeOff] Error approving request:', error);
    window.showToast?.(error?.message || 'Error approving request', 'error');
  }
}

async function denyRequest(id) {
  const note = (document.getElementById('review-note')?.value || '').trim().slice(0, 300);

  try {
    await _decideRequestViaCallable(id, 'deny', note);
    window.showToast?.('Request denied', 'success');
    closeModal('review');
  } catch (error) {
    console.error('[TimeOff] Error denying request:', error);
    window.showToast?.(error?.message || 'Error denying request', 'error');
  }
}

function deleteRequest(id) {
  const request = TimeOffState.requests.find(r => r.id === id);
  if (!request || request.status !== 'pending') return;

  document.getElementById('btn-confirm-delete-request').onclick = async () => {
    try {
      // TASK-4720 — withdraw = delete, requester only, server-side.
      const fn = httpsCallable(functions, 'saveTimeOffRequest');
      await fn({ tenantId: window.AppState?.tenant || 'demo', action: 'withdraw', requestId: id });
      window.showToast?.('Request withdrawn', 'success');
      closeModal('delete-request');
    } catch (error) {
      console.error('[TimeOff] Error withdrawing request:', error);
      window.showToast?.(error?.message || 'Error withdrawing request', 'error');
    }
  };

  document.getElementById('modal-delete-request').classList.remove('hidden');
}

// ============================================
// MODAL HELPERS
// ============================================
function closeModal(type) {
  document.getElementById(`modal-${type}`).classList.add('hidden');
  if (type === 'review') {
    TimeOffState.selectedRequest = null;
  }
}

// ============================================
// UTILITIES
// ============================================
// TASK-862 — delegates to shared window.escapeHtml (sharedFormat.js); escapes the
// identical set (&<>"', '->&#39;). Hoisted wrapper, window lookup deferred to call time.
function escapeHtml(str) {
  return window.escapeHtml(str);
}

function capitalizeFirst(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function formatDate(date) {
  if (!date) return '-';
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });
}

function formatDateTime(date) {
  if (!date) return '-';
  const d = date.toDate ? date.toDate() : new Date(date);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

function formatDateInput(date) {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  return d.toISOString().split('T')[0];
}

function truncate(str, maxLength) {
  if (!str) return '';
  return str.length > maxLength ? str.slice(0, maxLength) + '...' : str;
}

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
}

function calculateDays(start, end) {
  const startDate = start instanceof Date ? start : new Date(start);
  const endDate = end instanceof Date ? end : new Date(end);
  const diffTime = Math.abs(endDate - startDate);
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
}

function getTypeIcon(type) {
  const icons = {
    vacation: 'beach_access',
    sick: 'healing',
    personal: 'person',
    bereavement: 'favorite',
    parental_leave: 'child_care',
    jury_duty: 'gavel',
    days_on: 'event_available',
    other: 'event'
  };
  return icons[type] || 'event';
}

function formatType(type) {
  const types = {
    vacation: 'Vacation',
    sick: 'Sick Leave',
    personal: 'Personal Day',
    bereavement: 'Bereavement',
    parental_leave: 'Parental Leave',
    jury_duty: 'Jury Duty',
    days_on: 'Days On',
    other: 'Other'
  };
  return types[type] || type;
}

const BEREAVEMENT_TIER_LABELS = {
  level1: 'Level 1 — Spouse, child, parent, grandchild',
  level2: 'Level 2 — Sibling, in-laws',
  level3: 'Level 3 — Household member',
  level4: 'Level 4 — Grandparent, grandparent-in-law',
  level5: 'Level 5 — Loss of pregnancy',
  level6: 'Level 6 — Aunt, uncle, cousin, niece, nephew',
  level7: 'Level 7 — Distant relative, close friend',
};

function formatBereavementLevel(level) {
  return BEREAVEMENT_TIER_LABELS[level] || level || 'Unspecified';
}

// ============================================
// AUTO-FILL BEREAVEMENT HOURS FROM POLICY
// ============================================
function updateHoursHint() {
  const hoursInput = document.getElementById('request-hours');
  const hint = document.getElementById('request-hours-hint');
  if (!hoursInput || !hint) return;
  const hrs = parseFloat(hoursInput.value) || 0;
  if (hrs <= 0) { hint.textContent = ''; return; }
  const days = hrs / 8;
  if (Number.isInteger(days)) {
    hint.textContent = `${days} day${days !== 1 ? 's' : ''} (${hrs} hrs)`;
  } else {
    hint.textContent = `${days.toFixed(1)} days (${hrs} hrs)`;
  }
}

async function autofillBereavementHours() {
  const level = document.getElementById('timeoff-bereavement-level')?.value;
  const travel = document.getElementById('timeoff-bereavement-travel')?.checked;
  const hoursInput = document.getElementById('request-hours');
  const hint = document.getElementById('request-hours-hint');
  if (!level || !hoursInput) return;

  const personId = document.getElementById('request-person')?.value;
  const person = personId ? TimeOffState.people.find(p => p.id === personId) : null;
  if (!person?.hrTimeOffLevel || !person?.hrEmploymentType) return;

  try {
    const tenant = window.AppState?.tenant || TimeOffState.tenantId;
    if (!tenant) return;
    const policyDoc = await getDoc(doc(db, 'tenants', tenant, 'hrTimeOffPolicy', 'config'));
    if (!policyDoc.exists()) return;
    const policy = policyDoc.data();

    const policyLevel = policy.levels?.find(l => l.id === person.hrTimeOffLevel);
    if (!policyLevel) return;
    const allowances = policyLevel[person.hrEmploymentType] || {};
    const bObj = (typeof allowances.bereavement === 'object' && allowances.bereavement !== null)
      ? allowances.bereavement : null;
    if (!bObj) return;

    const bUnit = policy.bereavementUnit || 'days';
    let val = bObj[level] || 0;
    if (travel && bObj.travelDays) val += bObj.travelDays;
    const hrs = bUnit === 'hours' ? val : val * 8;
    hoursInput.value = hrs;
    if (hint) {
      hint.textContent = bUnit === 'hours'
        ? `${val} hr${val !== 1 ? 's' : ''} — per policy`
        : `${val} day${val !== 1 ? 's' : ''} (${hrs} hrs) — per policy`;
    }
  } catch (err) {
    console.warn('[TimeOff] Could not auto-fill bereavement hours:', err);
  }
}

// ============================================
// EXPOSE TO WINDOW FOR ONCLICK HANDLERS
// ============================================
window.TimeOffModule = {
  showNewRequest,
  editRequest,
  viewRequest,
  reviewRequest,
  saveRequest,
  approveRequest,
  denyRequest,
  deleteRequest,
  closeModal,
  autofillBereavementHours,
  updateHoursHint,
  toggleHistory
};

export default { initTimeOff };
